import type { TransientJobConfig } from "features/technical-data/scenario/lib/parseTrConfig";
import type { TechnicalDataScenario } from "features/technical-data/scenario/model/types";

import {
  findRecalculationMethod,
  findRoomFunctionVariant,
  ROOM_FUNCTION_VARIANT_VALUES,
  ROOM_FUNCTION_VARIANTS,
} from "../model/transientConfiguration";

type Translate = (key: string) => string;

export type TransientJobConfigRow = {
  label: string;
  value: string;
};

const roomFunctionLabel = (value: string, t: Translate) => {
  const variant =
    findRoomFunctionVariant(value) ??
    ROOM_FUNCTION_VARIANTS.find((item) => ROOM_FUNCTION_VARIANT_VALUES[item] === value);
  if (variant) return t(`Execution.transient.configuration.roomFunctionVariant.${variant}`);
  return value;
};

const recalculationLabel = (value: string, t: Translate) => {
  const method = findRecalculationMethod(value);
  if (method) return t(`Execution.transient.configuration.recalculationMethod.${method}`);
  return value;
};

export const resolveScenarioNames = (scenarioIds: number[], scenarios: TechnicalDataScenario[]) =>
  scenarioIds.map((id) => scenarios.find((scenario) => scenario.id === id)?.name ?? `#${id}`);

export const formatJobConfigRows = (
  config: TransientJobConfig,
  scenarioNames: string[],
  t: Translate
): TransientJobConfigRow[] => {
  const rows: TransientJobConfigRow[] = [];
  if (scenarioNames.length > 0) {
    rows.push({ label: t("Execution.transient.configuration.scenariosToRun"), value: scenarioNames.join(", ") });
  }
  if (config.time_interval_in_min != null) {
    rows.push({
      label: t("Execution.transient.configuration.timeIntervalInMin"),
      value: `${config.time_interval_in_min}`,
    });
  }
  if (config.recalculation_method) {
    rows.push({
      label: t("Execution.transient.configuration.recalculationMethod.label"),
      value: recalculationLabel(config.recalculation_method, t),
    });
  }
  if (config.t_air_exhaust != null) {
    rows.push({ label: t("Execution.transient.configuration.tAirExhaust"), value: `${config.t_air_exhaust}` });
  }
  if (config.delta_time_i != null) {
    rows.push({ label: t("Execution.transient.configuration.deltaTimeI"), value: `${config.delta_time_i}` });
  }
  if (config.room_function_variant) {
    rows.push({
      label: t("Execution.transient.configuration.roomFunctionVariant.label"),
      value: roomFunctionLabel(config.room_function_variant, t),
    });
  }
  if (config.percentile_to_consider != null) {
    rows.push({
      label: t("Execution.transient.configuration.percentileToConsider"),
      value: `${config.percentile_to_consider}`,
    });
  }
  if (config.dynamic_hvac_start_point != null) {
    rows.push({
      label: t("Execution.transient.configuration.dynamicHvacStartPoint"),
      value: config.dynamic_hvac_start_point ? t("general.yes") : t("general.no"),
    });
  }
  return rows;
};
